import Link from 'next/link';
import type React from 'react';
import { cn } from '../../features/shared/utils/cn';

const isHashFragment = (url: string) => url[0] === '#';

type MenuItemBaseProps = {
	title: string;
	icon?: React.ReactNode;
	className?: string;
	role?: string;
	'aria-label'?: string;
	'aria-expanded'?: boolean;
	forceHoverState?: boolean;
};

type MenuItemAsLink = MenuItemBaseProps & {
	href: string;
	onClick?: never;
};

type MenuItemAsButton = MenuItemBaseProps & {
	href?: never;
	onClick: () => void;
};

type MenuItemProps = MenuItemAsLink | MenuItemAsButton;

export const MenuItem = ({
	title,
	icon,
	href,
	onClick,
	forceHoverState,
	className,
	...props
}: MenuItemProps) => {
	const style = cn(
		'inline-flex gap-1 items-center',
		'text-black p-1 rounded-[4px]',
		forceHoverState ? 'text-white bg-primary-default' : '',
		'hover:text-white hover:bg-primary-default',
		'focus-visible:text-white focus-visible:bg-primary-default',
		'focus-visible:outline focus-visible:outline-2 focus-visible:outline-highlight',
		className,
	);
	const content = (
		<>
			<span className="text-[18px] leading-[21.6px] font-normal">{title}</span>
			{icon}
		</>
	);

	if (href) {
		// Hash fragments should use a plain anchor tag to trigger native hashchange events
		// which the HashDialog system relies on
		if (isHashFragment(href)) {
			return (
				<a
					href={href}
					className={cn('!underline', style, className)}
					{...props}
				>
					{content}
				</a>
			);
		}

		return (
			// Legacy behavior due to old next version. Cannot style next/link otherwise
			<Link href={href} legacyBehavior>
				<a className={cn('!underline', style, className)} {...props}>
					{content}
				</a>
			</Link>
		);
	}

	return (
		<button
			type="button"
			onClick={onClick}
			className={cn(style, className)}
			{...props}
		>
			{content}
		</button>
	);
};
